/**
 * PanelPromocja V2 — REAL DATA (Master Spec v4 growth loop: post → reach → visits → messages)
 *
 * Backend: GET /api/metrics/promo?days=30 (4 KPI + per-post performance + best post + platforms + scheduled + recommendations)
 *
 * Filozofia: nie kolejne mockware. Pokazujemy real efekty publikacji,
 * by zamknąć growth loop. Empty states zachęcają, gdy brak danych.
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  TrendingUp, Users, MessageSquare, Phone,
  Camera, Hash, Calendar, Clock, Trophy,
  Plus, Loader2, MoreHorizontal,
  Facebook, Search, Dog, ImageIcon,
  Rocket, Sparkles,
} from 'lucide-react';
import StudioShell from '@/components/studio/StudioShell';
import { API } from '@/utils/api';

const PRIMARY = '#F97316';

const PLATFORM_ICON = {
  facebook: Facebook,
  groups: Users,
  apisai: Dog,
  google: Search,
  instagram: Camera,
};

const REC_ICON = {
  trophy: Trophy,
  clock: Clock,
  camera: Camera,
  trending: TrendingUp,
  hash: Hash,
  rocket: Rocket,
  calendar: Calendar,
};

function DonutChart({ data, size = 160 }) {
  const total = data.reduce((s, d) => s + d.count, 0) || 1;
  const radius = size / 2 - 16;
  const cx = size / 2; const cy = size / 2;
  const c = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
      <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#1a1325" strokeWidth="16" />
      {data.map((d, i) => {
        const pct = d.count / total;
        const len = pct * c;
        const seg = <circle key={i} cx={cx} cy={cy} r={radius} fill="none" stroke={d.color} strokeWidth="16" strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset} />;
        offset += len;
        return seg;
      })}
    </svg>
  );
}

function KpiTile({ icon: Icon, label, value, delta, color }) {
  const isUp = (delta || 0) >= 0;
  const display = typeof value === 'number' ? value.toLocaleString('pl-PL') : value;
  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/5 p-4 sm:p-5 flex flex-col gap-3" data-testid={`kpi-${label.toLowerCase().split(' ')[0]}`}>
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}22`, border: `1px solid ${color}40` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <span className="text-zinc-400 text-sm font-medium">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-3xl sm:text-4xl font-bold text-white tabular-nums">{display}</span>
        {Number.isFinite(delta) && (
          <span className={`text-xs font-semibold ${isUp ? 'text-emerald-400' : 'text-rose-400'}`}>
            {isUp ? '↑' : '↓'} {Math.abs(delta)}%
          </span>
        )}
      </div>
    </div>
  );
}

function PostRow({ post }) {
  const Icon = PLATFORM_ICON[post.platform] || Dog;
  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.03] transition-colors" data-testid={`post-row-${post.id || ''}`}>
      <td className="py-3 px-2">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${post.platform_color}22`, border: `1px solid ${post.platform_color}40` }}>
            <Icon className="w-4 h-4" style={{ color: post.platform_color }} />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-medium truncate max-w-xs">{post.title}</p>
            <p className="text-zinc-500 text-[11px]">{post.platform_label} · {post.age}{post.has_photo ? ' · 📷' : ''}</p>
          </div>
        </div>
      </td>
      <td className="text-right py-3 px-2">
        <span className="text-white font-semibold tabular-nums">{(post.reach || 0).toLocaleString('pl-PL')}</span>
      </td>
      <td className="text-right py-3 px-2">
        <span className="text-zinc-300 tabular-nums">{post.visits || 0}</span>
      </td>
      <td className="text-right py-3 px-2">
        <span className="text-zinc-300 tabular-nums">{post.messages || 0}</span>
      </td>
    </tr>
  );
}

function PlatformBox({ icon: Icon, name, count, color }) {
  return (
    <div className="rounded-2xl bg-white/[0.02] border border-white/5 p-4 flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}22`, border: `1px solid ${color}40` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div>
        <p className="text-white font-semibold">{name}</p>
        <p className="text-zinc-500 text-xs">{count} {Number(count) === 1 ? 'publikacja' : 'publikacji'}</p>
      </div>
    </div>
  );
}

function ScheduledRow({ post }) {
  const Icon = PLATFORM_ICON[post.platform] || Calendar;
  const when = post.scheduled_at ? new Date(post.scheduled_at).toLocaleDateString('pl-PL', {
    weekday: 'long', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  }) : '—';
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5">
      <div className="w-9 h-9 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{post.title}</p>
        <p className="text-zinc-500 text-[11px]">
          {when} <span className="text-zinc-600 mx-0.5">·</span> {post.audience}
        </p>
      </div>
      <span className="px-2 py-1 rounded-full bg-amber-500/15 text-amber-400 text-[10px] font-bold uppercase flex-shrink-0">Zaplanowane</span>
    </div>
  );
}

function RecBox({ rec }) {
  const Icon = REC_ICON[rec.icon] || Sparkles;
  return (
    <div className="rounded-xl bg-white/[0.02] border border-white/5 p-4 flex gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${rec.color}22`, border: `1px solid ${rec.color}40` }}>
        <Icon className="w-5 h-5" style={{ color: rec.color }} />
      </div>
      <div className="min-w-0">
        <p className="text-white text-sm font-semibold">{rec.title}</p>
        <p className="text-zinc-400 text-xs mt-0.5 leading-snug">{rec.desc}</p>
      </div>
    </div>
  );
}

export default function PanelPromocja() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [kennel, setKennel] = useState(null);
  const [leadsCount, setLeadsCount] = useState(0);
  const [promo, setPromo] = useState(null);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    (async () => {
      try {
        const [kRes, lRes, pRes] = await Promise.allSettled([
          axios.get(`${API}/kennels`, { headers: headers() }),
          axios.get(`${API}/leads`, { headers: headers() }).catch(() => ({ data: [] })),
          axios.get(`${API}/metrics/promo?days=30`, { headers: headers() }),
        ]);
        const kennels = kRes.status === 'fulfilled' ? kRes.value.data : [];
        setKennel(Array.isArray(kennels) && kennels[0]);
        const leads = lRes.status === 'fulfilled' ? (lRes.value.data || []) : [];
        setLeadsCount(leads.length);
        setPromo(pRes.status === 'fulfilled' ? pRes.value.data : null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const traffic = useMemo(() => {
    if (!promo?.traffic_sources?.length) {
      return [{ label: 'Czekamy na pierwsze wizyty z postów…', count: 1, color: '#3F3457', pct: 100 }];
    }
    return promo.traffic_sources;
  }, [promo]);

  if (loading) {
    return <StudioShell mode="workspace" activeKey="promo" contextLabel="Promocja"><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 text-[var(--apisai-primary)] animate-spin" /></div></StudioShell>;
  }

  const k = promo?.kpis || {};
  const posts = promo?.posts || [];
  const best = promo?.best_post;
  const platforms = promo?.platforms || [];
  const scheduled = promo?.scheduled || [];
  const recommendations = promo?.recommendations || [];

  return (
    <StudioShell mode="workspace" activeKey="promo" contextLabel="Promocja">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6" data-testid="panel-promocja">
        {/* Header */}
        <header className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">Promocja i publikacje</h1>
            <p className="text-zinc-400 text-sm sm:text-base mt-1">
              {promo?.period_label ? `Real dane · ${promo.period_label}` : 'Twórz, planuj i analizuj swoje publikacje.'}
            </p>
          </div>
          <button onClick={() => navigate('/panel/publikacja')}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-semibold transition-colors shadow-lg shadow-[#F97316]/20"
            data-testid="new-post-cta">
            <Plus className="w-4 h-4" /> Nowy post
          </button>
        </header>

        {/* KPI strip — REAL DATA */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="promo-kpi">
          <KpiTile icon={TrendingUp} label="Zasięg postów" value={k.reach?.value ?? 0} delta={k.reach?.delta} color={PRIMARY} />
          <KpiTile icon={Users} label="Odwiedziny" value={k.visits?.value ?? 0} delta={k.visits?.delta} color="#3B82F6" />
          <KpiTile icon={MessageSquare} label="Wiadomości" value={k.messages?.value ?? 0} delta={k.messages?.delta} color="#10B981" />
          <KpiTile icon={Phone} label="Kliknięcia kontaktu" value={k.contact_clicks?.value ?? 0} delta={k.contact_clicks?.delta} color="#F59E0B" />
        </section>

        {/* Best post highlight */}
        {best && (
          <section className="rounded-3xl bg-gradient-to-r from-amber-500/15 via-amber-500/8 to-transparent border border-amber-500/30 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="best-post">
            <div className="w-14 h-14 rounded-2xl bg-amber-500/20 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
              <Trophy className="w-7 h-7 text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-amber-300 text-[10px] uppercase tracking-wider font-bold mb-1">Twój najlepszy post · {best.age}</p>
              <p className="text-white font-bold text-base sm:text-lg truncate">„{best.title}"</p>
              <p className="text-zinc-400 text-xs mt-1">
                Dotarł do <span className="text-white font-semibold">{(best.reach || 0).toLocaleString('pl-PL')}</span> osób
                {best.visits > 0 && <> · wywołał <span className="text-white font-semibold">{best.visits}</span> wizyt</>}
                {best.messages > 0 && <> · <span className="text-white font-semibold">{best.messages}</span> wiadomości</>}
              </p>
            </div>
            <button onClick={() => navigate('/panel/publikacja')}
              className="px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-zinc-900 text-sm font-bold rounded-xl transition-colors flex-shrink-0">
              Powtórz formułę
            </button>
          </section>
        )}

        {/* Performance table + donut */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-3xl bg-white/[0.02] border border-white/5 p-5 sm:p-6" data-testid="post-performance">
            <h3 className="text-white font-semibold mb-1">Skuteczność Twoich postów</h3>
            <p className="text-zinc-500 text-xs mb-4">Real metryki — odwiedziny i wiadomości w oknie 7 dni od publikacji</p>
            {posts.length === 0 ? (
              <div className="py-10 text-center">
                <div className="w-14 h-14 mx-auto rounded-2xl bg-violet-500/10 flex items-center justify-center mb-3">
                  <Rocket className="w-7 h-7 text-violet-300" />
                </div>
                <p className="text-white font-semibold">Brak publikacji w ostatnich 30 dniach</p>
                <p className="text-zinc-500 text-xs mt-1 mb-4">Pierwszy post odblokuje real metryki.</p>
                <button onClick={() => navigate('/panel/publikacja')}
                  className="px-4 py-2 rounded-xl bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-semibold">
                  Stwórz pierwszy post
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-2">
                <table className="w-full">
                  <thead>
                    <tr className="text-zinc-500 text-[11px] uppercase tracking-wider border-b border-white/10">
                      <th className="text-left py-2 px-2 font-medium">Post</th>
                      <th className="text-right py-2 px-2 font-medium">Zasięg</th>
                      <th className="text-right py-2 px-2 font-medium">Odwiedziny</th>
                      <th className="text-right py-2 px-2 font-medium">Wiadomości</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posts.map((p) => <PostRow key={p.id || p.created_at} post={p} />)}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="rounded-3xl bg-white/[0.02] border border-white/5 p-5 sm:p-6">
            <h3 className="text-white font-semibold mb-1">Skąd trafiają klienci?</h3>
            <p className="text-zinc-500 text-xs mb-4">Real sources z trackingu odwiedzin</p>
            <div className="flex items-center gap-4 mb-3">
              <DonutChart data={traffic} size={140} />
              <ul className="flex-1 space-y-1.5 text-xs">
                {traffic.map((s) => (
                  <li key={s.label || s.source} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: s.color }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-300 truncate text-[11px]">{s.label}</p>
                      <p className="text-zinc-500 text-[10px]">{s.pct}% ({s.count})</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Platforms */}
        <section data-testid="platforms">
          <h3 className="text-white font-semibold mb-1">Gdzie publikujesz?</h3>
          <p className="text-zinc-500 text-xs mb-4">Twoje kanały w ostatnich 30 dniach</p>
          {platforms.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-white/10 p-5 text-center">
              <p className="text-zinc-500 text-sm italic">Brak publikacji — opublikuj post aby zobaczyć rozkład kanałów.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {platforms.map((p) => {
                const Icon = PLATFORM_ICON[p.platform] || Dog;
                return <PlatformBox key={p.platform} icon={Icon} name={p.label} count={p.count} color={p.color} />;
              })}
            </div>
          )}
        </section>

        {/* Scheduled + Recommendations */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="rounded-3xl bg-white/[0.02] border border-white/5 p-5 sm:p-6" data-testid="scheduled">
            <div className="flex items-center justify-between mb-1">
              <h3 className="text-white font-semibold">Najbliższe zaplanowane publikacje</h3>
              <button className="text-zinc-400 hover:text-white"><MoreHorizontal className="w-4 h-4" /></button>
            </div>
            <p className="text-zinc-500 text-xs mb-4">Posty, które wkrótce się ukażą</p>
            {scheduled.length === 0 ? (
              <div className="py-6 text-center">
                <Calendar className="w-8 h-8 mx-auto text-zinc-600 mb-2" />
                <p className="text-zinc-500 text-xs italic">Brak zaplanowanych publikacji</p>
              </div>
            ) : (
              <div className="space-y-2">
                {scheduled.map((s) => <ScheduledRow key={s.id} post={s} />)}
              </div>
            )}
            <button onClick={() => navigate('/panel/publikacja')} className="mt-3 text-[#F97316] hover:text-[#FB923C] text-xs font-semibold">
              Zaplanuj publikację →
            </button>
          </div>

          <div className="rounded-3xl bg-white/[0.02] border border-white/5 p-5 sm:p-6" data-testid="recommendations">
            <h3 className="text-white font-semibold mb-1 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-violet-400" />
              Co zrobić, żeby publikacje działały lepiej?
            </h3>
            <p className="text-zinc-500 text-xs mb-4">Wskazówki bazujące na Twoich realnych danych</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {recommendations.map((r, i) => <RecBox key={i} rec={r} />)}
            </div>
          </div>
        </section>

        {/* Summary banner */}
        {(k.reach?.value > 0 || k.messages?.value > 0) && (
          <section className="rounded-3xl bg-gradient-to-r from-[#F97316]/15 to-[#EA580C]/10 border border-[#F97316]/20 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-6 h-6 text-emerald-400" />
            </div>
            <div className="flex-1">
              <p className="text-white font-bold text-base">Twoje publikacje przynoszą efekty</p>
              <p className="text-zinc-300 text-xs mt-1">
                Ostatnie 30 dni: <span className="text-white font-semibold">{(k.reach?.value || 0).toLocaleString('pl-PL')}</span> zasięgu,
                <span className="text-white font-semibold"> {k.visits?.value || 0}</span> odwiedzin,
                <span className="text-white font-semibold"> {k.messages?.value || 0}</span> wiadomości. Tak trzymaj!
              </p>
            </div>
            <button onClick={() => navigate('/panel/publikacja')}
              className="px-4 py-2.5 bg-[#F97316] hover:bg-[#EA580C] text-white text-sm font-semibold rounded-xl transition-colors flex-shrink-0">
              Utwórz nowy post
            </button>
          </section>
        )}
      </div>
    </StudioShell>
  );
}
